
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { getNotificationTemplates } from '@/utils/referral/notificationService';
import { NotificationTemplate } from '@/utils/referral/types';

interface NotificationTemplatesFormProps {
  onSaved: () => void;
}

// Define the form schema
const templateSchema = z.object({
  subject: z.string().min(2, { message: "Subject is required" }),
  content: z.string().min(10, { message: "Content is required" }),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

const NotificationTemplatesForm: React.FC<NotificationTemplatesFormProps> = ({ onSaved }) => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState<NotificationTemplate | null>(null);
  const { toast } = useToast();
  
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      subject: '',
      content: '',
    },
  });
  
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        const result = await getNotificationTemplates();
        setTemplates(result);
        if (result.length > 0) {
          setActiveTemplate(result[0]);
          form.reset({
            subject: result[0].subject || '',
            content: result[0].content || result[0].body, // Using body as fallback
          });
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        toast({
          title: "Error",
          description: "Failed to load notification templates",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTemplates();
  }, []);
  
  const handleSaveTemplate = async (data: TemplateFormValues) => {
    if (!activeTemplate) return;
    
    try {
      // Create an updated template with all required fields
      const updatedTemplate: NotificationTemplate = {
        ...activeTemplate,
        subject: data.subject,
        body: data.content, // Update the body field
        content: data.content, // Update the content field (extra)
        template: data.content, // For backward compatibility
      };
      
      // In a real app, we would save this to API
      console.log('Saving template:', updatedTemplate);
      
      toast({
        title: "Template Saved",
        description: "Notification template has been updated",
      });
      
      // Update the local state
      setTemplates(templates.map(t => 
        t.id === updatedTemplate.id ? updatedTemplate : t
      ));
      
      // Notify parent
      onSaved();
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save template",
        variant: "destructive",
      });
    }
  };
  
  const handleSelectTemplate = (templateId: string) => {
    const selected = templates.find(t => t.id === templateId);
    if (selected) {
      setActiveTemplate(selected);
      form.reset({
        subject: selected.subject || '',
        content: selected.content || selected.body, // Using body as fallback
      });
    }
  };
  
  if (isLoading) {
    return <div>Loading templates...</div>;
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Templates</CardTitle>
      </CardHeader>
      <CardContent>
        {templates.length > 0 ? (
          <Tabs defaultValue={templates[0].id}>
            <TabsList className="mb-6">
              {templates.map(template => (
                <TabsTrigger 
                  key={template.id} 
                  value={template.id}
                  onClick={() => handleSelectTemplate(template.id)}
                >
                  {template.name}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {templates.map(template => (
              <TabsContent key={template.id} value={template.id}>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSaveTemplate)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Subject</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="content"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Content</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field} 
                              rows={10} 
                              className="min-h-[200px]"
                              placeholder="Enter email content. You can use placeholders like {{user_name}} and {{referral_link}}"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit">Save Template</Button>
                  </form>
                </Form>
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="text-center py-6 text-gray-500">
            No notification templates found
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationTemplatesForm;
